/** @jsxImportSource @emotion/react */
// see - https://www.figma.com/file/pv2HV6DTSKY612VWeYWYdL/CE22-Design-System?node-id=48%3A264
import type { CSSProperties, ChangeEvent } from 'react';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import type { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import SelectMUI from '@mui/material/Select';
import NativeSelectMUI from '@mui/material/NativeSelect';
import { isMobile } from 'react-device-detect';
import MenuItem from '@mui/material/MenuItem';
import type { Theme } from '@emotion/react';
import { type SxProps, type MenuProps } from '@mui/material';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { match } from 'ts-pattern';
import type { TypographyVariant } from '../Typography';
import Typography, { useGetTypographyStyles } from '../Typography';
import type {
  IInputBaseProps,
  InputPropsToExcludeFromMuiProps,
  RenderInputAdornmentType,
} from '../Input/InputControlWrapper';
import InputControlWrapper, {
  INPUT_ADORNMENT_ICON_HEIGHT_PX,
  INPUT_HEIGHT,
  INPUT_HORIZONTAL_GAP_BETWEEN_ELEMENTS,
  INPUT_PADDING,
  INPUT_VERTICAL_GAP_BETWEEN_ELEMENTS,
} from '../Input/InputControlWrapper';
import {
  LIST_STYLES,
  getListItemStyles,
  getListItemTypographyVariant,
} from '../List';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import {
  DataElementId,
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';
import Checkbox from '../Checkbox';
import { useThemeToken } from '../../../hooks/use-get-theme/use-get-theme';

const LIST_ITEM_SIZE = 'medium';
const MULTIPLE_VALUES_SEPARATOR = ', ';

const MULTIPLE_ALL_NAME = 'All';
export const MULTIPLE_ALL_VALUE = 'ALL_OPTIONS_SELECTED';

export interface IOption<T extends string> extends Partial<IPropsWithTestId> {
  name: string;
  value: T;
  selectionDisabled?: boolean;
}

export interface ISelectComponentProps<T extends string>
  extends IPropsWithTestId,
  Omit<IInputBaseProps<T | T[]>, 'onChange'> {
  onChange: (newVal: T & T[]) => void;
  options: ReadonlyArray<Readonly<IOption<T>>>;
  multipleAllValueOptionName?: string;
  multipleAllValueIncludesOthers?: boolean;
  useStyledOpenIcon?: boolean;
  showPlaceholderInOptionsList?: boolean;
  suffix?: string;
  typographyVariant?: TypographyVariant;
  additionalFieldStyles?: CSSProperties;
  additionalItemListStyles?: SxProps<Theme>;
  additionalItemStyles?: CSSProperties;
}

const Select = <T extends string>(
  selectProps: Omit<
  SelectProps<T>,
  InputPropsToExcludeFromMuiProps | 'renderSuffix' | 'renderValue'
  > &
  ISelectComponentProps<T>,
) => {
  const {
    id,
    options,
    multiple = false,
    multipleAllValueOptionName = MULTIPLE_ALL_NAME,
    multipleAllValueIncludesOthers = false,
    defaultValue,
    value,
    onChange,
    placeholderText,
    helperText,
    customError,
    startAdornmentElement,
    endAdornmentElement,
    theme,
    useStyledOpenIcon = true,
    showPlaceholderInOptionsList = true,
    suffix,
    typographyVariant,
    additionalStyles,
    additionalFieldStyles = {},
    additionalItemListStyles = {},
    additionalItemStyles = {},
    inputProps,
    dataElementId,
    ...props
  } = selectProps;
  const themeToken = useThemeToken();

  // The nature of the state is determined during the first render.
  // It's considered controlled if the value is not `undefined`.
  const controlledValue = useMemo(
    () => (typeof value === 'string' ? value ?? '' : value ?? []),
    [value],
  );

  const [latestSelectedValue, setLatestSelectedValue] =
    useState<typeof controlledValue>(controlledValue);

  useEffect(() => setLatestSelectedValue(controlledValue), [controlledValue]);

  const useMultipleAsSingle = useMemo(
    () => multiple && Array.isArray(controlledValue) && options.length <= 1,
    [multiple, options, controlledValue],
  );

  const useMultiple = useMemo(
    () => multiple && Array.isArray(controlledValue) && !useMultipleAsSingle,
    [multiple, useMultipleAsSingle, controlledValue],
  );

  const multipleAllValueOption: Readonly<IOption<T>> = {
    name: multipleAllValueOptionName,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    value: MULTIPLE_ALL_VALUE as T,
    dataElementId: DataElementId.SELECT_ALL_VALUE,
  };

  const getRealValueForMultiple = useCallback(
    (newValue: string[]): string[] =>
      match({
        multipleAllValueIncludesOthers,
        areAllOptionsSelected: !options.find(
          (opt) => !newValue.find((x) => x === opt.value),
        ),
        isNewValueNotEmpty: !!newValue.length,
        isNewValueWithAllValue: !!newValue.find(
          (x) => x === multipleAllValueOption.value,
        ),
        lastAddedValue: newValue.length ? newValue[newValue.length - 1] : null,
      })
        .with(
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          { lastAddedValue: multipleAllValueOption.value as string },
          {
            multipleAllValueIncludesOthers: true,
            areAllOptionsSelected: true,
          },
          () => [multipleAllValueOption.value],
        )
        .with(
          {
            multipleAllValueIncludesOthers: true,
            isNewValueWithAllValue: true,
          },
          // All options except the selected one, since the previous state was all options
          () =>
            options
              .filter((opt) => opt.value !== newValue[newValue.length - 1])
              .map((x) => x.value),
        )
        .with(
          {
            multipleAllValueIncludesOthers: false,
            isNewValueWithAllValue: true,
          },
          // All options except 'ALL'
          () => newValue.filter((x) => x !== multipleAllValueOption.value),
        )
        .with({ isNewValueNotEmpty: false }, () => [
          !latestSelectedValue?.includes(multipleAllValueOption.value) &&
          options.length > 1
            ? multipleAllValueOption.value
            : options[0].value,
        ])
        .otherwise(() => newValue),
    [
      latestSelectedValue,
      multipleAllValueIncludesOthers,
      multipleAllValueOption.value,
      options,
    ],
  );

  const getRealSelectValueForSingleOrMultiple = useCallback(
    (selectValue: T | T[] | string) => {
      // On autofill we get a stringified value.
      const newValue =
        typeof selectValue === 'string' && multiple
          ? selectValue.split(MULTIPLE_VALUES_SEPARATOR)
          : selectValue;

      if (Array.isArray(newValue) && useMultiple) {
        return getRealValueForMultiple(newValue);
      }

      // Multiselect drop-down should be selected by default if there is only one choice
      if (
        Array.isArray(newValue) &&
        useMultipleAsSingle &&
        !newValue.length &&
        !!options.length
      ) {
        return [options[0].value];
      }

      return newValue;
    },
    [
      multiple,
      useMultiple,
      useMultipleAsSingle,
      options,
      getRealValueForMultiple,
    ],
  );

  const handleSelect = useCallback(
    (selectValue: T | T[] | string) => {
      const realValue = getRealSelectValueForSingleOrMultiple(selectValue);
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      setLatestSelectedValue(realValue as T | T[]);
      if (
        !useMultiple &&
        JSON.stringify(realValue) !== JSON.stringify(controlledValue)
      ) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        onChange(realValue as T & T[]);
      }
    },
    [
      getRealSelectValueForSingleOrMultiple,
      onChange,
      useMultiple,
      controlledValue,
    ],
  );

  const handleClose = useCallback(() => {
    if (
      useMultiple &&
      latestSelectedValue !== undefined &&
      JSON.stringify(latestSelectedValue) !== JSON.stringify(controlledValue)
    ) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      onChange(latestSelectedValue as T & T[]);
    }
  }, [useMultiple, latestSelectedValue, onChange, controlledValue]);

  useEffect(() => {
    if (
      (useMultiple || useMultipleAsSingle) &&
      !latestSelectedValue?.length &&
      !!options.length
    ) {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      handleSelect([]);
    }
  }, [
    latestSelectedValue,
    handleSelect,
    useMultiple,
    useMultipleAsSingle,
    options.length,
  ]);

  if (multiple && controlledValue && !Array.isArray(controlledValue)) {
    throw new Error(
      'If `multiple` is `true`, `value` must be an array and the Select will support multiple selections.',
    );
  }

  const renderEndAdornmentForSelect = (
    renderEndAdornment: RenderInputAdornmentType,
  ) =>
    (endAdornmentElement ? renderEndAdornment(endAdornmentElement) : undefined);

  const renderOpenIconForSelect = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    iconProps: any,
    renderEndAdornment: RenderInputAdornmentType,
  ) => {
    const openIconStyles = {
      '&.MuiSelect-icon, &.MuiNativeSelect-icon': {
        top: `calc(50% - ${INPUT_ADORNMENT_ICON_HEIGHT_PX / 2}px)`,
      },
    };

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <div css={openIconStyles} {...iconProps}>
        {renderEndAdornment(faAngleDown, 'select-open-icon')}
      </div>
    );
  };

  const getInputValue = (selectValue: T | T[] | undefined): T | undefined =>
    (Array.isArray(selectValue)
      ? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (selectValue.join(MULTIPLE_VALUES_SEPARATOR) as T)
      : selectValue);

  const renderValue = (selectValue: T | T[] | undefined) => {
    if (
      Array.isArray(selectValue) &&
      selectValue.length === 1 &&
      selectValue[0] === multipleAllValueOption.value
    ) {
      return multipleAllValueOption.name;
    }

    return (Array.isArray(selectValue) ? selectValue : [selectValue])
      .map((x) => options.find((opt) => opt.value === x)?.name)
      .join(MULTIPLE_VALUES_SEPARATOR);
  };

  const getOptionKey = (option: IOption<T>) =>
    `${option?.value} - ${option?.name}`;

  const getOptionValue = (option: IOption<T>) =>
    `${option?.name}${suffix || ''}`;

  const handleMuiSelect = (e: SelectChangeEvent<T | T[]>) => {
    handleSelect(e.target.value);
  };

  const showHiddenOptionInNativeSelect =
    !latestSelectedValue &&
    !options.find((x) => x.value === latestSelectedValue);

  const handleNativeSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    if (showHiddenOptionInNativeSelect) {
      // Lose an element focus to force list of options to rerender on iOS mobile devices
      e.currentTarget.blur();
    }
    handleSelect(e.target.value);
  };

  const getOptionLabelElementId = (option: Readonly<IOption<T>> | undefined) =>
    option?.dataElementId ?? `${dataElementId}-option-${option?.name}`;

  const renderLabel = (option: Readonly<IOption<T>> | undefined) => (
    <Typography
      variant={
        typographyVariant ??
        getListItemTypographyVariant(
          LIST_ITEM_SIZE,
          option?.value === latestSelectedValue,
        )
      }
      theme={theme}
      dataElementId={getOptionLabelElementId(option)}
    >
      {option ? getOptionValue(option) : placeholderText ?? ''}
    </Typography>
  );

  const fieldTypographyStyles = {
    ...useGetTypographyStyles(
      typographyVariant ??
        getListItemTypographyVariant(LIST_ITEM_SIZE, undefined),
      theme,
      themeToken,
    ),
    textTransform: undefined,
  };

  const placeholderTypographyStyles = useGetTypographyStyles(
    typographyVariant ??
      getListItemTypographyVariant(LIST_ITEM_SIZE, undefined),
    theme,
    themeToken,
    theme.colors.grayscale.inactiveGrey1,
  );
  const placeholderStyles = placeholderText
    ? {
      content: `"${placeholderText}"`,
      ...placeholderTypographyStyles,
    }
    : {};

  const fieldStyles = {
    ...fieldTypographyStyles,
    cursor: 'pointer',
    'label + &': {
      marginTop: 0,
    },
    '&::before':
      isMobile && !latestSelectedValue
        ? {
          ...placeholderStyles,
          borderBottom: '0 !important',
          left: INPUT_PADDING,
          bottom: 'unset',
        }
        : { display: 'none' },
    '&::after': {
      display: 'none',
    },
    '& .MuiSelect-select, & .MuiNativeSelect-select': {
      // 12px - width of the open icon;
      // added to avoid overriding the icon if input is with a long value
      paddingRight: `calc(${INPUT_PADDING} + 12px) !important`,
      div: fieldTypographyStyles,
      display: 'inline-block',
      '& .notranslate::after': placeholderStyles,
    },
    '& .MuiSelect-icon, & .MuiNativeSelect-icon': {
      display: 'flex',
      alignItems: 'center',
      marginRight: additionalFieldStyles?.paddingRight ?? INPUT_PADDING,
      paddingRight: '0 !important',
      paddingLeft: 0,
      // Absolute - to override the icon with an input area
      // so the icon remains clickable and active
      position: 'absolute',
    },
    '> div:last-child, > svg:last-child': endAdornmentElement
      ? {
        // To avoid hiding the open icon, move the end adornment
        // to the right to fit the width of the open icon + padding between elements.
        marginRight: `calc(12px + ${INPUT_HORIZONTAL_GAP_BETWEEN_ELEMENTS})`,
      }
      : {},
  };

  const menuProps: Partial<MenuProps> = {
    // To avoid moving autofocus to the end of the options list
    // when deselecting any option after ALL was selected
    autoFocus: !useMultiple,
    sx: {
      marginTop: INPUT_VERTICAL_GAP_BETWEEN_ELEMENTS,
      '& .MuiPaper-root': {
        maxHeight: '264px',
        // eslint-disable-next-line react/jsx-props-no-spreading
        ...LIST_STYLES,
      },
      '& ul': {
        padding: 0,
      },
      ...additionalItemListStyles,
    },
  };

  const menuItemStyles = {
    ...getListItemStyles(LIST_ITEM_SIZE, theme),
    height: INPUT_HEIGHT,
    padding: INPUT_PADDING,
  };

  const placeholderMenuItemStyles = {
    '&.Mui-disabled': {
      backgroundColor: theme.colors.grayscale.white,
      opacity: 1,
      '> div': {
        color: theme.colors.grayscale.inactiveGrey1,
      },
    },
  };

  // Use native select for mobile devices if multiple options are not available
  const useNativeSelect = isMobile && !useMultiple;

  return (
    <InputControlWrapper
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...selectProps}
      defaultValue={getInputValue(defaultValue)}
      value={getInputValue(latestSelectedValue)}
      onChange={() => {}}
      dataElementId={dataElementId}
      renderInput={({
        renderStartAdornment,
        renderEndAdornment,
        inputStyles,
        id: controlId,
      }) =>
        (useNativeSelect ? (
          <NativeSelectMUI
            id={controlId}
            sx={{
              ...inputStyles({ ...fieldStyles, ...additionalFieldStyles }),
              '& .MuiInputBase-input.MuiNativeSelect-select': {
                margin: additionalFieldStyles?.border ? '1px' : undefined,
                // To hide the first option when no value is selected
                color: !latestSelectedValue ? 'transparent' : undefined,
                option: {
                  ...menuItemStyles,
                  ...fieldTypographyStyles,
                  ...additionalItemStyles,
                },
              },
            }}
            startAdornment={renderStartAdornment(startAdornmentElement)}
            endAdornment={renderEndAdornmentForSelect(renderEndAdornment)}
            IconComponent={
              useStyledOpenIcon
                ? (iconProps) =>
                  renderOpenIconForSelect(iconProps, renderEndAdornment)
                : undefined
            }
            defaultValue={defaultValue}
            value={latestSelectedValue}
            onChange={handleNativeSelect}
            onClose={handleClose}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...{
              [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-native-select`,
            }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          >
            {/* Add a hidden option to be able to select the first option
                when no value is selected */}
            {showHiddenOptionInNativeSelect && (
              <option
                value={latestSelectedValue}
                hidden
                selected
                style={{ display: 'none' }}
              />
            )}
            {options?.map((option) => {
              const optionKey = getOptionKey(option);
              const optionId = `${dataElementId}-option-${option.value}`;
              return (
                <option
                  key={optionKey}
                  value={option?.value}
                  disabled={option?.selectionDisabled}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...{
                    [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: optionId,
                    [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: optionId,
                  }}
                >
                  {getOptionValue(option)}
                </option>
              );
            })}
          </NativeSelectMUI>
        ) : (
          <SelectMUI
            id={controlId}
            sx={inputStyles({
              ...fieldStyles,
              ...additionalFieldStyles,
            })}
            startAdornment={renderStartAdornment(startAdornmentElement)}
            endAdornment={renderEndAdornmentForSelect(renderEndAdornment)}
            IconComponent={
              useStyledOpenIcon
                ? (iconProps) =>
                  renderOpenIconForSelect(iconProps, renderEndAdornment)
                : undefined
            }
            multiple={useMultiple}
            defaultValue={defaultValue}
            value={latestSelectedValue}
            onChange={handleMuiSelect}
            onClose={handleClose}
            renderValue={renderValue}
            MenuProps={menuProps}
            inputProps={inputProps}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...{ [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: `${dataElementId}-select` }}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...props}
          >
            {!!placeholderText && showPlaceholderInOptionsList && (
              <MenuItem
                sx={{
                  ...menuItemStyles,
                  ...placeholderMenuItemStyles,
                  ...additionalItemStyles,
                }}
                key={placeholderText}
                value={undefined}
                disabled
              >
                {renderLabel(undefined)}
              </MenuItem>
            )}
            {[...(useMultiple ? [multipleAllValueOption] : []), ...options].map(
              (option) => {
                const optionKey = getOptionKey(option);
                const optionId = `${dataElementId}-option-${option.value}`;
                return (
                  <MenuItem
                    sx={{ ...menuItemStyles, ...additionalItemStyles }}
                    key={optionKey}
                    value={option?.value}
                    disabled={option?.selectionDisabled}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    {...{
                      [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: optionId,
                      [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: optionId,
                    }}
                  >
                    {useMultiple && (
                      <Checkbox
                        label=""
                        checked={
                          !!latestSelectedValue &&
                          ((latestSelectedValue.includes(
                            multipleAllValueOption.value,
                          ) &&
                            multipleAllValueIncludesOthers) ||
                            latestSelectedValue.indexOf(option.value) > -1)
                        }
                        indeterminate={
                          option === multipleAllValueOption &&
                          !!latestSelectedValue?.length &&
                          !latestSelectedValue.includes(
                            multipleAllValueOption.value,
                          )
                        }
                        dataElementId={`${getOptionLabelElementId(
                          option,
                        )}-checkbox`}
                        variant="default"
                        theme={theme}
                        additionalStyles={{
                          marginRight: 0,
                          '.MuiCheckbox-root': { paddingTop: '9px' },
                        }}
                      />
                    )}
                    {renderLabel(option)}
                  </MenuItem>
                );
              },
            )}
          </SelectMUI>
        ))}
    />
  );
};

export default Select;
